import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import api from "../api/api";

const CategoryManager = () => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState({ name: "", description: "" });
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 🔹 Catégories protégées (ne peuvent pas être modifiées ou supprimées)
    const protectedCategories = ["Autre", "location"];

    // 🔹 Vérifier si une catégorie est protégée
    const isProtectedCategory = (categoryName) => {
        return protectedCategories.includes(categoryName);
    };

    // 🔹 Fetch all categories
    const fetchCategories = async () => {
        try {
            setLoading(true);
            const res = await api.get("/categories");
            setCategories(res.data);
        } catch (err) {
            console.error("Error fetching categories:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    // 🔹 Add or update category
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newCategory.name.trim()) return;

        // Empêcher la création de catégories avec des noms protégés
        if (!editing && isProtectedCategory(newCategory.name)) {
            setError(t('reserved_cat_error'));
            return;
        }

        try {
            if (editing) {
                await api.put(`/categories/${editing.id}`, newCategory);
            } else {
                await api.post("/categories", newCategory);
            }

            await fetchCategories();
            setNewCategory({ name: "", description: "" });
            setEditing(null);
            setError(null);
        } catch (err) {
            setError(err.message);
        }
    };

    // 🔹 Edit category
    const handleEdit = (cat) => {
        if (isProtectedCategory(cat.name)) {
            setError(t('protected_cat_error'));
            return;
        }
        setNewCategory({ name: cat.name, description: cat.description || "" });
        setEditing(cat);
        setError(null);
    };

    // 🔹 Delete category
    const handleDelete = async (id) => {
        const categoryToDelete = categories.find(cat => cat.id === id);

        if (categoryToDelete && isProtectedCategory(categoryToDelete.name)) {
            setError(t('protected_cat_error'));
            return;
        }

        const result = await Swal.fire({
            title: t('confirm_delete_title'),
            text: t('confirm_delete_text'),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: t('delete_btn'),
            cancelButtonText: t('cancel')
        });

        if (!result.isConfirmed) return;

        try {
            await api.delete(`/categories/${id}`);
            await fetchCategories();
            setError(null);
            Swal.fire({
                icon: 'success',
                title: t('delete_success'),
                timer: 1500,
                showConfirmButton: false
            });
        } catch (err) {
            setError(t('delete_error'));
        }
    };

    return (
        <div className={`bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-8 mb-8 border border-white/40 ${isRtl ? 'rtl text-right' : 'ltr text-left'}`} dir={isRtl ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className={`flex items-center justify-between mb-8 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <div className={`p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg ${isRtl ? 'ml-4' : 'mr-4'}`}>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                    </div>
                    <div className={isRtl ? 'text-right' : 'text-left'}>
                        <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                            {t('manage_categories_title')}
                        </h2>
                        <p className="text-gray-500 text-sm mt-1">{t('category_desc')}</p>
                    </div>
                </div>
                {editing && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2">
                        <span className="text-amber-800 text-sm font-medium">✏️ {t('edit_mode_active')}</span>
                    </div>
                )}
            </div>

            {/* Message d'erreur */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
                    <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <div className={`text-red-500 text-lg ${isRtl ? 'ml-3' : 'mr-3'}`}>⚠️</div>
                        <p className="text-red-700 font-medium">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className={`${isRtl ? 'mr-auto' : 'ml-auto'} text-red-500 hover:text-red-700`}
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl p-6 mb-8 border border-gray-200">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            {t('category_name_label')} *
                        </label>
                        <input
                            type="text"
                            placeholder={t('category_placeholder')}
                            value={newCategory.name}
                            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                            className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200 ${isRtl ? 'text-right' : 'text-left'}`}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            {t('description_label')}
                        </label>
                        <input
                            type="text"
                            placeholder={t('description_placeholder')}
                            value={newCategory.description}
                            onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                            className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200 ${isRtl ? 'text-right' : 'text-left'}`}
                        />
                    </div>
                </div>
                <div className={`flex flex-wrap gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <button
                        type="submit"
                        className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95"
                    >
                        <span className="font-semibold">
                            {editing ? `💾 ${t('save_category')}` : `➕ ${t('add_category')}`}
                        </span>
                    </button>
                    {editing && (
                        <button
                            type="button"
                            onClick={() => {
                                setEditing(null);
                                setNewCategory({ name: "", description: "" });
                                setError(null);
                            }}
                            className="flex items-center space-x-2 bg-gradient-to-r from-gray-400 to-gray-500 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95"
                        >
                            <span>❌ {t('cancel')}</span>
                        </button>
                    )}
                </div>
            </form>

            {/* Liste des catégories */}
            {loading ? (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <p className="text-gray-600 mt-3">{t('loading_categories')}</p>
                </div>
            ) : categories.length === 0 ? (
                <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl p-12 text-center border border-gray-200">
                    <div className="text-gray-400 text-4xl mb-4">📁</div>
                    <h3 className="text-gray-600 font-semibold text-lg mb-2">{t('no_categories_found')}</h3>
                    <p className="text-gray-500">{t('empty_categories_desc')}</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
                    <div className={`bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                        <div className={`flex items-center justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <h3 className="text-lg font-semibold text-gray-800">
                                {t('existing_categories')} ({categories.length})
                            </h3>
                            <div className="text-sm text-gray-500">
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-lg text-xs">
                                    🔒 {protectedCategories.length} {t('protected_categories_count')}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr className={isRtl ? 'flex-row-reverse' : ''}>
                                    <th className={`px-6 py-4 text-sm font-semibold text-gray-700 ${isRtl ? 'text-right' : 'text-left'}`}>{t('name_col')}</th>
                                    <th className={`px-6 py-4 text-sm font-semibold text-gray-700 ${isRtl ? 'text-right' : 'text-left'}`}>{t('description_col')}</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">{t('actions_col')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {categories.map((cat) => {
                                    const isProtected = isProtectedCategory(cat.name);
                                    return (
                                        <tr key={cat.id} className="hover:bg-blue-50/30 transition-colors duration-200">
                                            <td className="px-6 py-4">
                                                <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                                    <span className={`font-medium text-gray-800 ${isRtl ? 'text-right' : 'text-left'}`}>{cat.name}</span>
                                                    {isProtected && (
                                                        <span className={`${isRtl ? 'mr-2' : 'ml-2'} bg-blue-100 text-blue-800 px-2 py-1 rounded-lg text-xs font-medium`}>
                                                            🔒
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`text-gray-600 ${isRtl ? 'text-right' : 'text-left'}`}>{cat.description || "-"}</span>
                                            </td>

                                            <td className="px-6 py-4">
                                                <div className={`flex justify-center ${isRtl ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                                                    <button
                                                        onClick={() => handleEdit(cat)}
                                                        disabled={isProtected}
                                                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 font-medium ${isProtected
                                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                                                            }`}
                                                    >
                                                        <span>✏️</span>
                                                        <span>{t('edit_btn')}</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(cat.id)}
                                                        disabled={isProtected}
                                                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 font-medium ${isProtected
                                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                : "bg-red-50 text-red-600 hover:bg-red-100"
                                                            }`}
                                                    >
                                                        <span>🗑️</span>
                                                        <span>{t('delete_btn')}</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryManager;
